import { Component, ChangeDetectorRef, NgZone, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { VALID_DEEP_LINK_PATHS, VALID_DEEP_LINK_PREFIXES } from './app.routes';
import {
  IonApp, IonRouterOutlet, IonSplitPane, IonMenu, IonHeader, IonToolbar,
  IonContent, IonList, IonItem, IonLabel, IonIcon, IonMenuToggle,
  IonFooter, IonButtons, IonButton,
  ToastController, AlertController, ModalController
} from '@ionic/angular/standalone';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Storage } from '@ionic/storage-angular';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { SwUpdate } from '@angular/service-worker';
import { UtilsService } from '../services/utils.service';
import { GoogleAuthService } from '../services/google-auth.service';
import { UserService } from '../services/user.service';
import { TeamDataService } from '../services/team-data.service';
import { FirebaseService } from '../services/firebase.service';
import { NavigationService } from '../services/navigation.service';
import { IntercomService } from '../services/intercom.service';
import { ChargebeeService } from '../services/chargebee.service';

declare const gtag: any;

interface PageConfig {
  title: string;
  route: string;
  icon?: string;
  faIcon: string;
  active: boolean;
  basicHide: boolean;
  hideFlag: string;
  name: string;
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    CommonModule,
    TranslateModule,
    IonApp, IonRouterOutlet, IonSplitPane, IonMenu, IonHeader, IonToolbar,
    IonContent, IonList, IonItem, IonLabel, IonIcon, IonMenuToggle,
    IonFooter, IonButtons, IonButton
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  pages: PageConfig[] = [];
  dismissedAnnouncement: string = '';
  loggedIn: boolean = false;
  errorToastVisible: boolean = false;
  private teamDataSubscription?: Subscription;
  private userLoggedOutSubscription?: Subscription;
  private userLoggedInSubscription?: Subscription;
  private errorSubjectSubscription?: Subscription;
  private openChatSubjectSubscription?: Subscription;
  private openPageSubscription?: Subscription;
  private openUrlSubscription?: Subscription;
  private openExtUrlSubscription?: Subscription;
  private serverDataReadySubscription?: Subscription;
  private teamDataLoadingSubscription?: Subscription;
  
  constructor(
    private translate: TranslateService,
    private storage: Storage,
    private platform: Platform,
    public utils: UtilsService,
    private googleAuth: GoogleAuthService,
    public user: UserService,
    public teamData: TeamDataService,
    private firebase: FirebaseService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private ngZone: NgZone,
    private nav: NavigationService,
    private intercom: IntercomService,
    private chargebeeService: ChargebeeService,
    private swUpdate: SwUpdate
  ) {
    // Capture deep link URL before any navigation happens
    this.captureDeepLink();

    this.initializeApp();
    this.setupSubscriptions();
    this.initializeGoogleAuth();
    this.initializeFirebase();
    this.initializeIntercom();
    this.chargebeeService.init();
    this.setupPlatformDetection();
    this.setupNetworkListeners();
    this.checkForAppUpdate();
    this.initializePages();
  }

  // capture initial URL for deep linking (must be called before any navigation)
  private captureDeepLink() {
    const path = window.location.pathname;

    // Check if path is a valid deep link (uses constants from app.routes.ts)
    const isValidPath = VALID_DEEP_LINK_PATHS.includes(path) ||
                        VALID_DEEP_LINK_PREFIXES.some(prefix => path.startsWith(prefix));

    if (path && isValidPath) {
      // Store full path with query string for complete deep link
      this.utils.pendingDeepLink = path + window.location.search;
      this.utils.logDebug('Captured deep link: ' + this.utils.pendingDeepLink);

      // Set flag if this is a user/member view deep link (no underlying view in nav stack)
      if (path.startsWith('/user/')) {
        this.utils.isDeepLinkedUserView = true;
      }
    }
  }

  ngOnInit() {
    // Global change detection for TeamData updates
    // This replicates AngularFire2's automatic change detection behavior.
    // We use global change detection (vs per-component subscriptions) because:
    // 1. TeamData changes infrequently (load once, rarely update)
    // 2. Most components display team data - global approach is simpler
    // 3. Maintains compatibility with existing component patterns from old codebase
    // 4. Performance impact is minimal due to low update frequency
    this.teamDataSubscription = this.teamData.teamUpdated$.subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy() {
    if (this.teamDataSubscription) {
      this.teamDataSubscription.unsubscribe();
    }
    if (this.userLoggedOutSubscription) {
      this.userLoggedOutSubscription.unsubscribe();
    }
    if (this.userLoggedInSubscription) {
      this.userLoggedInSubscription.unsubscribe();
    }
    if (this.errorSubjectSubscription) {
      this.errorSubjectSubscription.unsubscribe();
    }
    if (this.openChatSubjectSubscription) {
      this.openChatSubjectSubscription.unsubscribe();
    }
    if (this.openPageSubscription) {
      this.openPageSubscription.unsubscribe();
    }
    if (this.openUrlSubscription) {
      this.openUrlSubscription.unsubscribe();
    }
    if (this.openExtUrlSubscription) {
      this.openExtUrlSubscription.unsubscribe();
    }
    if (this.serverDataReadySubscription) {
      this.serverDataReadySubscription.unsubscribe();
    }
    if (this.teamDataLoadingSubscription) {
      this.teamDataLoadingSubscription.unsubscribe();
    }
  }

  async initializeApp() {
    // initialize storage
    await this.storage.create();
    
    // set default language
    this.translate.setDefaultLang('en');
    
    // check for stored language preference
    try {
      const lang = await this.storage.get('language');
      if (lang && typeof lang === 'string' && lang !== '' && lang !== 'default') {
        this.translate.use(lang);
      } else {
        this.translate.use('en');
      }
    } catch (error) {
      this.utils.logError('Error loading language preference', error);
      this.translate.use('en');
    }
  }

  // set up subscriptions to utility service subjects
  private setupSubscriptions() {
    // handle open url events initiated elsewhere
    this.openUrlSubscription = this.utils.openUrl.subscribe((url: string) => {
      this.openUrl(url);
    });

    // handle open external url events initiated elsewhere
    // copied from old codebase app.component.ts lines 448-450
    this.openExtUrlSubscription = this.utils.openExtUrl.subscribe((url: string) => {
      this.openExtUrl(url);
    });

    // when logged out, turn off intercom and go to welcome (login) view
    // copied from old codebase app.component.ts lines 418-437
    this.userLoggedOutSubscription = this.user.loggedOut.subscribe(() => {
      this.loggedIn = false;
      // Shutdown Intercom
      this.intercom.shutdown();
      // must make all pages inactive here so upon next login we know to set it
      const activePage = this.pages.find(p => p.active);
      if (activePage) {
        activePage.active = false;
      }
      this.utils.logDebug('redirecting after logout');
      // Use NavigationService with the special logout method that preserves the exact working logic
      this.nav.navigateForLogout('/welcome');
    });

    // handle errors from providers
    // copied from old codebase app.component.ts lines 291-293
    this.errorSubjectSubscription = this.utils.errorSubject.subscribe((error) => {
      this.displayError(error);
    });

    // handle open chat calls from providers  
    // copied from old codebase app.component.ts lines 296-298
    this.openChatSubjectSubscription = this.utils.openChatSubject.subscribe(() => {
      this.openChat();
    });

    // handle page changes initiated elsewhere
    // copied from old codebase app.component.ts lines 440-442
    this.openPageSubscription = this.utils.openPage.subscribe((pageName: string) => {
      this.openPage(pageName);
    });

    // tasks to do when user logs in
    // copied from old codebase app.component.ts lines 352-366
    this.userLoggedInSubscription = this.user.loggedIn.subscribe((service) => {
      if (service === 'none') { // shouldn't happen
        this.utils.logDebug('got loggedIn event but no service; redirecting to login page');
        // Use NavigationService to preserve query params
        this.nav.navigate('/welcome', { replaceUrl: true });
      } else {
        if (typeof gtag !== 'undefined') {
          gtag('set', 'user_data', { "email": this.user.email });
          gtag('event', 'login', {
            'send_to': [
              'G-0ZT86RG6WC', 
              'AW-940533138/awBqCK_R9YIdEJLLvcAD'
            ],
            'type': 'signup',
            'method': 'Google',
            'value': 1.0,
            'currency': 'USD'
          });
        }
        this.loggedIn = true;
      }  
    });

    // upon any refresh ending, init intercom, call ganalytics, and check for setup
    // copied from old codebase app.component.ts lines 377-404
    this.serverDataReadySubscription = this.teamData.serverDataReady.subscribe(() => {
      this.utils.logDebug('serverDataReady received');
      // if first load, don't have info to know which page to open til now
      this.openInitialPage();
      // Initialize Intercom (ok to call every time, it checks if already initialized)
      this.intercom.init(this.teamData.intercomTokens, this.teamData.isSupportHidden());
      // Check for setup triggers - copied from old codebase app.component.ts lines 386-398
      this.checkSetupTriggers();
      // Track successful installation - copied from old codebase app.component.ts lines 399-403
      if (this.teamData.metaData.permission === 'full' && typeof gtag !== 'undefined') {
        gtag('set', 'user_data', { "email": this.user.email });
        gtag('event', 'installed', {
          'send_to': [
            'G-0ZT86RG6WC', 
            'AW-940533138/QmEiCLLR9YIdEJLLvcAD'
          ],
          'type': 'signup',
          'value': 10.0,
          'currency': 'USD'
        });
      }
    });

    // upon load check for new data that could change home page or intercom state
    // copied from old codebase app.component.ts lines 407-415
    this.teamDataLoadingSubscription = this.teamData.loading.subscribe((_state: boolean) => {
      // First initialization may have wrong intercom visibility if subscription support field not set
      // and / or admin / owner status not yet known
      if (this.intercom.isInitialized()) {
        this.intercom.setVisibility(this.teamData.isSupportHidden());
      }
      // if already logged in we have local data, don't have to wait til data loads, so go to home now
      // if page already set this does nothing
      this.openInitialPage();
    });
  }

  // triggered by openUrl subject in utils; open in same window if browser, in system browser if app
  // copied from old codebase app.component.ts lines 755-763
  openUrl(url: string) {
    if (Capacitor.isNativePlatform()) {
      window.open(url, '_system');
    } else {
      window.open(url, '_self');
    }
  }

  // open external URL (mailto:, tel:, sms:, http:)
  // copied and adapted from old codebase app.component.ts lines 765-775
  openExtUrl(url: string) {
    if (Capacitor.isNativePlatform()) {
      // On native platforms, use system browser/handler
      window.open(url, '_system');
    } else {
      // On web, open http/mailto in new tab, other protocols in same window
      if (url.startsWith('http') || url.startsWith('mailto')) {
        window.open(url, '_blank');
      } else {
        // For tel: and sms: links, use location.href to trigger native handler
        window.location.href = url;
      }
    }
  }

  // initialize Google Authentication service
  private initializeGoogleAuth() {
    this.googleAuth.initialize();
  }

  // initialize Firebase service
  private initializeFirebase() {
    this.firebase.initialize();
  }

  // initialize Intercom support chat (load script for web)
  private initializeIntercom() {
    // Check if embedded mode (disable Intercom)
    if (!Capacitor.isNativePlatform()) {
      const embedInputs = this.utils.getUrlInputs('embed');
      if (embedInputs.path === 'embed' || embedInputs.param === 'true') {
        this.utils.embedded = true;
        this.utils.intercomDisable = true;
      }
    }
    // Load Intercom script (web only, will check internally)
    this.intercom.loadScript();
  }

  // setup platform detection using same logic as original codebase
  private setupPlatformDetection() {
    // Wait for platform to be ready before detecting
    this.platform.ready().then(() => {

      // Detect PWA mode (must happen before platform detection which uses this.utils.pwa)
      if (window.matchMedia('(display-mode: standalone)').matches || document.referrer.includes('android-app://')) {
        this.utils.pwa = true;
      }

      // Determine platform using same logic as original team-data.ts
      if (this.platform.is('android')) {
        if (Capacitor.isNativePlatform()) {
          this.utils.platform = 'android';
        } else if (this.utils.pwa) {
          this.utils.platform = 'pwa-android';
        } else {
          this.utils.platform = 'web-android';
        }
      } else if (this.platform.is('ios')) {
        if (Capacitor.isNativePlatform()) {
          this.utils.platform = 'ios';
        } else if (this.utils.pwa) {
          this.utils.platform = 'pwa-ios';
        } else {
          this.utils.platform = 'web-ios';
        }
      } else if (this.platform.is('mobile')) {
        // Mobile platform detected but not specifically Android/iOS
        // This handles our URL parameter override case
        if (this.utils.pwa) {
          this.utils.platform = 'pwa-mobile';
        } else {
          this.utils.platform = 'web-mobile';
        }
      } else if (this.utils.pwa) {
        this.utils.platform = 'pwa';
      } else {
        this.utils.platform = 'web';
      }

      this.utils.logDebug('platform=' + this.utils.platform);
    });
  }

  // setup network online/offline listeners with toasts and data recovery
  private setupNetworkListeners() {
    // get immediate value for startup
    if (window.navigator.onLine === false) {
      this.utils.offlineSubject.next(true);
      this.intercom.hide();
      this.utils.logInfo('network is offline');
    } else {
      this.utils.offlineSubject.next(false);
      this.intercom.show();
      this.utils.logInfo('network is online');
    }
    // subscribe to online / offline events
    window.addEventListener('offline', () => {
      this.utils.offlineSubject.next(true);
      this.intercom.hide();
      if (this.utils.pwa) {
        this.utils.errorSubject.next('Network disconnected');
      }
      this.utils.logDebug('Network disconnected');
    });
    window.addEventListener('online', () => {
      // per docs need to wait prior to doing any api requests
      setTimeout(() => {
        this.ngZone.run(() => {
          this.utils.offlineSubject.next(false);
          let startedOffline: boolean = !this.user.id; // if not there, app was started while offline
          if (startedOffline) {
            this.googleAuth.refreshTokens(true).then(() => { // passing true populates user as well
              this.teamData.load('google').then(() => {
                this.intercom.init(this.teamData.intercomTokens, this.teamData.isSupportHidden());
              });
            });
          } else {
            this.googleAuth.refreshTokens().then(() => {
              this.intercom.show();
            });
          }
          if (this.utils.pwa) {
            this.utils.errorSubject.next('Connection back!');
          }
          this.utils.logDebug('network was re-connected with startedOffline = ' + startedOffline);
        });
      }, 3000);
    });
  }

  // check for app updates via service worker and auto-reload if available
  // Reason: Angular's ngsw uses cache-first strategy, so new deployments aren't
  // picked up until the service worker detects the updated hash manifest and
  // activates the new version. On iOS PWA, killing the app doesn't always
  // terminate the service worker, so updates can be missed without this.
  private checkForAppUpdate() {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    // reload when the service worker has a new version ready
    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        const prev = (event as any).currentVersion?.hash || 'unknown';
        const next = (event as any).latestVersion?.hash || 'unknown';
        this.utils.logInfo('App update: ' + prev.substring(0, 8) + ' → ' + next.substring(0, 8) + ', reloading');
        document.location.reload();
      } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
        this.utils.logError('App update failed: ' + ((event as any).error || 'unknown error'));
      }
    });

    // recover from broken service worker state (e.g. failed asset download)
    this.swUpdate.unrecoverable.subscribe((event) => {
      this.utils.logError('Service worker unrecoverable: ' + event.reason);
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((r) => r.unregister());
        document.location.reload();
      });
    });

    // check on startup
    this.swUpdate.checkForUpdate().catch((err) => {
      this.utils.logDebug('Update check failed: ' + err);
    });

    // check when app resumes from background (iOS PWA kill/restart)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.swUpdate.checkForUpdate().catch((err) => {
          this.utils.logDebug('Update check on resume failed: ' + err);
        });
      }
    });
  }

  // initialize pages for menu navigation
  private initializePages() {
    this.pages = [
      { title: 'PHONE_LIST', route: '/phone-list', faIcon: 'list', active: false, basicHide: false, hideFlag: 'hidePhonelist', name: 'list' },
      { title: 'GALLERY', route: '/gallery', faIcon: 'user', active: false, basicHide: false, hideFlag: 'hideGallery', name: 'gallery' },
      { title: 'TABLE', route: '/table', faIcon: 'table', active: false, basicHide: false, hideFlag: 'hideTable', name: 'table' },
      { title: 'TIMELINE', route: '/timeline', faIcon: 'clock-o', active: false, basicHide: true, hideFlag: 'hideTimeline', name: 'timeline' },
      { title: 'CALENDAR', route: '/calendar', faIcon: 'calendar-o', active: false, basicHide: true, hideFlag: 'hideCalendar', name: 'calendar' },
      { title: 'ORG_CHART', route: '/orgchart', faIcon: 'sitemap', active: false, basicHide: true, hideFlag: 'hideOrgchart', name: 'orgchart' },
    ];

    // hide PHONE_LIST view if not a phone
    if (!this.isPhone()) {
      this.pages = this.pages.filter(p => p.title !== 'PHONE_LIST');
    }

    // load announcement from storage
    this.storage.get('announcement').then((storedAnnouncement: string) => {
      if (storedAnnouncement && typeof storedAnnouncement === 'string') {
        this.dismissedAnnouncement = storedAnnouncement;
      }
    }).catch((reason: any) => {
      this.utils.logInfo('error getting announcement from local data', reason);
    });
  }

  // check if device is phone
  private isPhone(): boolean {
    // Check for URL parameter override first (for testing)
    const urlParams = new URLSearchParams(window.location.search);
    const platformParam = urlParams.get('ionicplatform');
    if (platformParam === 'ios' || platformParam === 'android') {
      return true;
    }

    // Fall back to normal detection
    const isMobile = this.platform.is('mobile');
    const width = this.platform.width();
    const isPhone = isMobile && width < 768;
    return isPhone;
  }

  // get color for basic mode bar
  // Note: old codebase used 'danger' which was orange (#f0ad4e)
  // In new theme, that orange is now 'tertiary'
  getBasicBarColor(): string {
    return this.teamData.metaData.subscriptionData.stayBasic ? 'light' : 'tertiary';
  }

  // check if menu item should be hidden
  isMenuHidden(page: PageConfig): boolean {
    if (!this.teamData || this.teamData.metaData.permission === 'unknown') {
      // real team hasn't loaded yet, so hide everything
      return true;
    }
    const subData = this.teamData.metaData.subscriptionData;
    return (subData as any)?.[page.hideFlag] || (page.basicHide && this.teamData.basicMode && (subData as any)?.stayBasic);
  }

  // show announcement in footer
  showAnnouncement(): boolean {
    const currentAnnouncement = this.teamData.metaData.subscriptionData.announcement;
    if (currentAnnouncement && currentAnnouncement !== '') {
      return this.dismissedAnnouncement !== currentAnnouncement;
    }
    return false;
  }

  // dismiss announcement
  dismissAnnouncement() {
    this.dismissedAnnouncement = this.teamData.metaData.subscriptionData.announcement || '';
    this.storage.set('announcement', this.dismissedAnnouncement).catch((reason: any) => {
      this.utils.logInfo('error setting announcement in local data', reason);
    });
  }

  // check if setup modal needs to be shown
  // copied from old codebase app.component.ts lines 386-398
  checkSetupTriggers() {
    // Don't open if already open
    if (this.teamData.setupOpen) {
      return;
    }

    const permission = this.teamData.metaData.permission;
    const subscriptionData = this.teamData.metaData.subscriptionData;

    if (permission === 'no api access') {
      this.utils.logDebug('opening setup, no api access');
      this.openSetupPage();
    } else if ((this.teamData.basicMode && !subscriptionData.stayBasic) ||
               this.teamData.metaData.membersTruncated) {
      // basicMode is true when adminEmail not set (permission not 'full')
      // if cache has been deleted popup will not come up (ok, rare)
      this.utils.logDebug('opening setup, basicMode=' + this.teamData.basicMode +
                          ', stayBasic=' + subscriptionData.stayBasic +
                          ', membersTruncated=' + this.teamData.metaData.membersTruncated);
      this.openSetupPage();
    }
  }

  // open setup page modal
  // backdropDismiss: false prevents closing by clicking outside when setup is required
  async openSetupPage() {
    if (this.teamData.setupOpen) {
      return; // Don't open twice
    }
    const { SetupPage } = await import('./pages/setup/setup.page');
    const modal = await this.modalCtrl.create({
      component: SetupPage,
      backdropDismiss: false
    });
    this.teamData.setupOpen = true;
    await modal.present();
  }

  // open chat/support
  // copied from old codebase app.component.ts openChat() lines 695-704
  openChat() {
    this.intercom.openChat(this.teamData.isSupportHidden());
  }

  // display error toast
  // copied and adapted from old codebase app.component.ts lines 707-741
  async displayError(error: any) {
    // special handler for gmail alert
    if (error === 'Gmail addresses are not supported. Please use your business email address.') {
      const confirm = await this.alertCtrl.create({
        header: 'Account not found',
        message: error,
        buttons: [
          {
            text: 'OK',
            handler: () => {
              location.reload(); // had to do this for some reason otherwise attachLoginToElement does not work
            }
          }
        ]
      });
      await confirm.present();
      return;
    }
    // to avoid buffering up a bunch while user is away (happened for PWA on computer)
    if (this.errorToastVisible) {
      return;
    }
    this.errorToastVisible = true;
    const toast = await this.toastCtrl.create({
      message: error,
      position: 'top',
      duration: 4000,
      cssClass: 't2-toast-error',
      buttons: [
        {
          text: 'Close',
          role: 'cancel'
        }
      ]
    });
    toast.onDidDismiss().then(() => {
      this.errorToastVisible = false;
    });
    await toast.present();
  }

  // open page navigation method
  // copied and adapted from old codebase app.component.ts lines 478-561
  openPage(pageTitle: string) {
    let page: PageConfig | undefined;
    if (pageTitle === 'home') {
      if (typeof this.platform === 'undefined') {
        this.utils.logError('app.component openPage called with undefined platform');
        pageTitle = 'GALLERY';
      } else if (this.isPhone()) {
        pageTitle = 'PHONE_LIST';
      } else if (this.teamData.metaData.subscriptionData.hideGallery) {
        const firstUnhiddenPage = this.pages.find(p => !this.isMenuHidden(p));
        if (firstUnhiddenPage) {
          pageTitle = firstUnhiddenPage.title;
        } else {
          pageTitle = 'GALLERY'; // everything is hidden
        }
      } else {
        pageTitle = 'GALLERY';
      }
      // TODO: Add URL direct linking logic when needed
    }

    // get page object
    if (!page) {
      page = this.pages.find(p => p.title === pageTitle);
    } 
    if (!page) {
      this.utils.logError('openPage next called with bad page name: '+pageTitle);
      return;
    }

    // bail if this page is already active
    if (page.active) {
      this.utils.logDebug('page already active, so not setting it');
      return;
    }

    // send events to intercom and google analytics
    // copied from old codebase app.component.ts lines 530-544
    const pageName = pageTitle.split('_').join('').toLowerCase();
    const eventName = 'viewed-' + pageName;
    this.intercom.trackEvent(eventName);
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        'event_category': 'engagement'
      });
    }

    // Use NavigationService to preserve query params
    // replaceUrl prevents browser history accumulation on root views,
    // which avoids iOS back-swipe navigating away from the current view
    this.nav.navigate(page.route, { replaceUrl: true });

    // highlighting
    page.active = true;
    for (let p of this.pages) {  
      if (p.title === page.title) {
        p.active = true;
      } else {
        p.active = false;
      }
    }  
  }

  // open initial page on startup
  // copied from old codebase app.component.ts lines 563-569, enhanced with deep link support
  openInitialPage() {
    // only set if no page is set yet and metaData is present so we know which page to go to
    if (!this.pages.find(p => p.active) && this.teamData.metaData.subscriptionData.cbId !== '') {
      // Check for pending deep link first
      if (this.utils.pendingDeepLink) {
        const deepLink = this.utils.pendingDeepLink;
        this.utils.pendingDeepLink = null; // Clear it so we don't navigate again
        this.utils.logDebug('Navigating to deep link: ' + deepLink);

        if (this.utils.isDeepLinkedUserView) {
          // For member view deep links, navigate to home first (replaceUrl)
          // then forward to member view, so back button/swipe returns to home
          this.utils.isDeepLinkedUserView = false;
          this.openPage('home');
          setTimeout(() => {
            this.router.navigateByUrl(deepLink);
          }, 0);
        } else {
          // For root view deep links, navigate directly with replaceUrl
          this.router.navigateByUrl(deepLink, { replaceUrl: true });
          const matchingPage = this.pages.find(p => deepLink.startsWith(p.route));
          if (matchingPage) {
            this.pages.forEach(p => p.active = false);
            matchingPage.active = true;
          }
        }
        return;
      }
      this.openPage('home');
    }
  }
}
